/** Иконки витрины: один штрих, один язык.
 *
 * DESIGN.md запрещает эмодзи в роли иконок — у них чужая пластика, свой цвет
 * и разный рисунок на каждой платформе. Здесь единый набор: stroke 2.2,
 * currentColor, скруглённые концы — те же манеры, что у стрелок навигации.
 */

/** weight — толщина штриха: в нижней полосе иконки тоньше (1.9), чем в теле
    экрана, и билет там должен выглядеть соседом, а не гостем. */
type Props = { className?: string; size?: number; weight?: number };

function Glyph({ children, className, size = 20, weight = 2.2 }: Props & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  );
}

export const CartGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M3 5h2.2l2.5 10.4a1.7 1.7 0 0 0 1.7 1.3h7.3a1.7 1.7 0 0 0 1.7-1.3L20 8H6.4" />
    <circle cx="10" cy="20" r="1.3" />
    <circle cx="17" cy="20" r="1.3" />
  </Glyph>
);

/** Огонь: сгорание паркоинов — пламя с язычком внутри. */
export const FlameGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M12 21c-3.6 0-6-2.4-6-5.6 0-2.7 1.7-4.6 3.2-6.2.9-1 1.6-2.1 1.8-3.7 2.6 1.4 4.3 3.6 4.5 6.1.7-.6 1.1-1.4 1.3-2.4 1.4 1.4 2.2 3.4 2.2 5.5C19 18.4 15.9 21 12 21Z" />
    <path d="M12 21c-1.5 0-2.6-1.1-2.6-2.6 0-1.4 1-2.4 2-3.4.3 1 1 1.7 1.8 2.1.3-.4.5-.9.6-1.5.6.7 1 1.6 1 2.6 0 1.8-1.2 2.8-2.8 2.8Z" />
  </Glyph>
);

/** Прыжок: стрелка вверх над линией земли — кнопка в зигзаге. */
export const JumpGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M6.5 10.5 12 5l5.5 5.5M12 5v10" />
    <path d="M5 19.5h14" />
  </Glyph>
);

export const BoltGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />
  </Glyph>
);

export const GiftGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M3.5 11.5h17V21h-17z" />
    <path d="M2.5 7.5h19v4h-19zM12 7.5V21" />
    <path d="M12 7.5S10.6 3 8.2 3a2.2 2.2 0 0 0 0 4.5H12Zm0 0S13.4 3 15.8 3a2.2 2.2 0 0 1 0 4.5H12Z" />
  </Glyph>
);

export const PinGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.6" />
  </Glyph>
);

export const TrashGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M4 6.5h16M9.5 6.5V4h5v2.5M6.5 6.5 7.6 20a1.5 1.5 0 0 0 1.5 1.4h5.8a1.5 1.5 0 0 0 1.5-1.4L17.5 6.5" />
  </Glyph>
);

export const PlusGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M12 5.5v13M5.5 12h13" />
  </Glyph>
);

export const MinusGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M5.5 12h13" />
  </Glyph>
);

export const CloseGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Glyph>
);

export const ChevronGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="m9 5 7 7-7 7" />
  </Glyph>
);

export const ClockGlyph = (p: Props) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.3l3.2 2" />
  </Glyph>
);

export const CoinGlyph = (p: Props) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.2v9.6M14.6 9.4c-.6-.8-1.6-1.2-2.6-1.2-1.5 0-2.6.8-2.6 2s1.1 1.8 2.6 2 2.6.8 2.6 2-1.1 2-2.6 2c-1.1 0-2-.4-2.6-1.2" />
  </Glyph>
);

export const ShareGlyph = (p: Props) => (
  <Glyph {...p}>
    <circle cx="6.5" cy="12" r="2.5" />
    <circle cx="17.5" cy="6" r="2.5" />
    <circle cx="17.5" cy="18" r="2.5" />
    <path d="m8.8 10.8 6.4-3.4M8.8 13.2l6.4 3.4" />
  </Glyph>
);

export const ChatGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M21 12.4c0 4-4 7.2-9 7.2-1 0-2-.1-2.9-.4L4 21l1.4-3.6C4 16.1 3 14.4 3 12.4 3 8.4 7 5.2 12 5.2s9 3.2 9 7.2Z" />
  </Glyph>
);

export const BagGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M5 8h14l-1 12.4a1.6 1.6 0 0 1-1.6 1.4H7.6A1.6 1.6 0 0 1 6 20.4L5 8Z" />
    <path d="M9 8V6.2a3 3 0 0 1 6 0V8" />
  </Glyph>
);

export const CheckGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="m4.5 12.5 5 5L20 7" />
  </Glyph>
);

/** Билетик розыгрыша: выемки по бокам и линия отрыва — узнаётся сразу. */
export const TicketGlyph = (p: Props) => (
  <Glyph {...p}>
    <path d="M4 6.5h16a1 1 0 0 1 1 1V10a2 2 0 0 0 0 4v2.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V14a2 2 0 0 0 0-4V7.5a1 1 0 0 1 1-1Z" />
    <path d="M15 7v1.6M15 11.2v1.6M15 15.4V17" />
  </Glyph>
);
